// import React from 'react';
import axios, { AxiosError } from 'axios';

const BASE_URL = 'https://inventariosbackend.velasresorts.com/inventarios/facturas';

const facturasApi = {
    // ── Carga XML (CFDI) ─────────────────────────────────────────────────────
    parseXml: async (xmlFile: File): Promise<any> => {
        try {
            const formData = new FormData();
            formData.append('xml', xmlFile);

            const response = await fetch(`${BASE_URL}/parse-xml`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json().catch(() => null);

            if (!response.ok) {
                return {
                    error: (data && data.error) || 'El archivo no es un CFDI válido',
                    reason: data && data.reason,
                };
            }

            return data;
        } catch (error) {
            console.error('Error al parsear el XML de la factura:', error);
            return { error: 'Error de red al parsear el XML' };
        }
    },

    importar: async (pdfFile: File | null, data: any) => {
        try {
            const formData = new FormData();
            if (pdfFile) formData.append('pdf', pdfFile);
            formData.append('data', JSON.stringify(data));

            const response = await fetch(`${BASE_URL}/importar`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error ${response.status}: ${errorText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error al importar la factura:', error);
            return null;
        }
    },

    // ── Carga manual ─────────────────────────────────────────────────────────
    crearManual: async (pdfFile: File | null, data: any) => {
        try {
            const formData = new FormData();
            if (pdfFile) formData.append('pdf', pdfFile);
            formData.append('data', JSON.stringify(data));

            const response = await fetch(`${BASE_URL}/crear`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error ${response.status}: ${errorText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error al crear la factura manual:', error);
            return null;
        }
    },

    // ── Listado / detalle ────────────────────────────────────────────────────
    listado: async (hotelIds?: number[]) => {
        try {
            const params = hotelIds && hotelIds.length > 0 ? { hotelIds: hotelIds.join(',') } : undefined;
            const response = await axios.get(`${BASE_URL}/listado`, {
                params,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            return Array.isArray(response.data) ? response.data : (response.data?.data || []);
        } catch (error) {
            console.error('Error fetching listado de facturas:', error);
            return [];
        }
    },

    cancelar: async (id: string | number) => {
        try {
            const response = await axios.put(`${BASE_URL}/${id}/cancelar`, {}, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError;
            console.error('Error al cancelar la factura:', error);
            return { success: false, error: (axiosError.response?.data as any)?.error || 'Error de red al cancelar la factura' };
        }
    },

    detalle: async (id: string | number) => {
        try {
            const response = await axios.get(`${BASE_URL}/${id}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError;
            if (axiosError.response) {
                console.error('Error response data:', axiosError.response.data);
                console.error('Error response status:', axiosError.response.status);
            } else if (axiosError.request) {
                console.error('Error request:', axiosError.request);
            } else {
                console.error('Error message:', axiosError.message);
            }
            return null;
        }
    },

    // ── Relacionar artículos ─────────────────────────────────────────────────
    relacionarNuevoActivo: async (facturaId: number, conceptoId: number, payload: any, imagenFile?: File | null) => {
        try {
            const formData = new FormData();
            formData.append('data', JSON.stringify(payload));
            if (imagenFile) formData.append('imagen', imagenFile);

            const response = await fetch(`${BASE_URL}/${facturaId}/conceptos/${conceptoId}/nuevo-activo`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json().catch(() => null);

            if (!response.ok) {
                return { success: false, error: (data && data.error) || `Error ${response.status} al crear el activo` };
            }

            return data;
        } catch (error) {
            console.error('Error al relacionar nuevo activo con la factura:', error);
            return { success: false, error: 'Error de red al crear el activo' };
        }
    },

    relacionarActivoExistente: async (facturaId: number, conceptoId: number, activoId: number) => {
        try {
            const response = await axios.post(
                `${BASE_URL}/${facturaId}/conceptos/${conceptoId}/activo-existente`,
                { activoId },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error al relacionar activo existente con la factura:', error);
            return null;
        }
    },

    buscarActivosParaRelacionar: async (params: {
        serial?: string;
        marcaId?: string | number;
        modeloId?: string | number;
        hotelId?: string | number;
        q?: string;
    }) => {
        try {
            const response = await axios.get(`${BASE_URL}/activos/buscar`, {
                params,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            return Array.isArray(response.data) ? response.data : (response.data?.data || []);
        } catch (error) {
            console.error('Error al buscar activos para relacionar:', error);
            return [];
        }
    },

    // ── Descargas ────────────────────────────────────────────────────────────
    descargarPdfUrl: (id: number | string) => `${BASE_URL}/${id}/descargar-pdf`,
    descargarXmlUrl: (id: number | string) => `${BASE_URL}/${id}/descargar-xml`,
    descargarDetalleUrl: (id: number | string) => `${BASE_URL}/${id}/descargar-detalle`,
};

export default facturasApi;
